import React from 'react'
import { ChevronLeftIcon, ChevronRightIcon, Loader2 } from 'lucide-react'
import { clsx } from 'clsx'
import { Button } from '../Button'
import { OverflowText } from '../OverflowText'

export interface PaginationInfo {
  page: number
  limit: number
  total: number
  pages: number
}

export interface DataTableProps<T> {
  data: T[]
  columns: Array<{
    key: keyof T | 'actions'
    title: string
    width?: string
    /**
     * Иконка вместо текста в заголовке (для узких колонок): текст `title`
     * уходит в `aria-label` и во всплывающую подсказку.
     */
    headerIcon?: React.ReactNode
    render?: (value: any, item: T) => React.ReactNode
  }>
  loading?: boolean
  pagination?: (PaginationInfo & {
    onPageChange: (page: number) => void
  }) | undefined
  onRowClick?: (item: T) => void
  className?: string
  emptyMessage?: string
  loadingMessage?: string
  fillHeight?: boolean
  /** Компактные отступы ячеек и заголовков (таблица заказов); по умолчанию — прежние px-6 py-4 */
  dense?: boolean
  /** table-fixed + w-full: ширины колонок в процентах, таблица не шире контейнера */
  fixedLayout?: boolean
}

// `column.width` исторически задан как «хвост» Tailwind-класса (w-64 → '64'),
// поэтому такие значения ни на что не влияли. Инлайн-стилем ширину применяем
// только в фиксированной раскладке (`fixedLayout`) и только для настоящих
// CSS-ширин: таблицы остальных экранов должны рендериться как раньше.
const CSS_WIDTH_RE = /^(?:\d+(?:\.\d+)?(?:px|%|rem|em|vw|vh)|auto|fit-content)$/

const columnWidthStyle = (width?: string): React.CSSProperties | undefined => {
  const value = width?.trim()
  if (!value || !CSS_WIDTH_RE.test(value)) return undefined
  return { width: value }
}

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  pagination,
  onRowClick,
  className,
  emptyMessage = 'Нет данных для отображения',
  loadingMessage = 'Загрузка...',
  fillHeight = false,
  dense = false,
  fixedLayout = false,
}: DataTableProps<T>) {
  if (loading) {
    return (
      <div className={clsx('admin-card', className)}>
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-3">
            <Loader2 className="h-6 w-6 animate-spin text-gray-600" />
            <span className="text-gray-500 font-medium">{loadingMessage}</span>
          </div>
        </div>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className={clsx('admin-card', className)}>
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Нет данных</h3>
          <p className="text-gray-500">{emptyMessage}</p>
        </div>
      </div>
    )
  }

  const totalPages = pagination ? pagination.pages : 1
  const currentPage = pagination ? pagination.page : 1

  // `.admin-table td/th` из index.css задаёт px-6/py-4/whitespace-nowrap и
  // специфичнее обычных утилит (класс + элемент), поэтому компактные отступы
  // и разрешённый перенос подаём с `!`. Остальные таблицы идут по прежней ветке.
  // px-3 на xl не берём: брейкпоинт 1280px уже накрывает 1366, а лишние 8px
  // отступа в каждой колонке съедают место там, где таблица должна влезать.
  const cellPaddingClassName = dense ? '!px-2 !py-2' : undefined
  const headerExtraClassName = clsx(cellPaddingClassName, fixedLayout && 'min-w-0')
  const cellExtraClassName = clsx(
    cellPaddingClassName,
    dense && '!whitespace-normal',
    fixedLayout && 'min-w-0'
  )

  // Генерация номеров страниц для отображения
  const getPageNumbers = () => {
    const delta = 2 // Количество страниц с каждой стороны от текущей
    const range = []
    const rangeWithDots = []

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i)
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...')
    } else {
      rangeWithDots.push(1)
    }

    rangeWithDots.push(...range)

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages)
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages)
    }

    return rangeWithDots
  }

  return (
    <div className={clsx(
      'admin-card overflow-hidden',
      fillHeight && 'flex flex-col min-h-0',
      className
    )}>
      {/* Таблица */}
      <div className={fillHeight ? 'flex-1 min-h-0 overflow-auto' : 'overflow-x-auto'}>
        <table className={clsx('admin-table', fixedLayout && 'table-fixed w-full')}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  // Инлайн-ширина нужна только фиксированной раскладке: в остальных
                  // таблицах ширины колонок не применялись и применять их нельзя —
                  // разметка этих экранов должна остаться прежней.
                  style={fixedLayout ? columnWidthStyle(column.width) : undefined}
                  className={clsx(
                    fillHeight && 'sticky top-0 z-10 bg-white',
                    headerExtraClassName,
                    // Прежний (недействующий) класс оставлен ради неизменной
                    // разметки таблиц без fixedLayout.
                    !fixedLayout && column.width && `w-${column.width}`
                  )}
                >
                  <div className="flex items-center space-x-1">
                    {column.headerIcon ? (
                      // Узкая колонка: текст заголовка не влезает, показываем иконку.
                      // Полное название — в aria-label и в подсказке.
                      <OverflowText text={column.title} alwaysShowTooltip className="flex">
                        <span role="img" aria-label={column.title} className="inline-flex">
                          {column.headerIcon}
                        </span>
                      </OverflowText>
                    ) : fixedLayout ? (
                      // В фиксированной раскладке колонки узкие: длинный заголовок
                      // переносится максимум на две строки, а полный текст остаётся
                      // в подсказке — иначе заголовок растягивает строку и таблицу.
                      <OverflowText text={column.title} maxLines={2} />
                    ) : (
                      <span>{column.title}</span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item, index) => (
              <tr
                key={index}
                className={clsx(
                  'transition-colors',
                  onRowClick && 'cursor-pointer hover:bg-gray-50'
                )}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map((column) => (
                  <td key={String(column.key)} className={cellExtraClassName || undefined}>
                    {column.render
                      ? column.render(item[column.key as keyof T], item)
                      : String(item[column.key as keyof T] || '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Пагинация */}
      {pagination && totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          {/* Мобильная пагинация */}
          <div className="flex-1 flex justify-between sm:hidden">
            <Button
              variant="secondary"
              onClick={() => pagination.onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              size="sm"
            >
              Назад
            </Button>
            <Button
              variant="secondary"
              onClick={() => pagination.onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              size="sm"
            >
              Далее
            </Button>
          </div>

          {/* Десктопная пагинация */}
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Показано{' '}
                <span className="font-medium">
                  {(currentPage - 1) * pagination.limit + 1}
                </span>{' '}
                -{' '}
                <span className="font-medium">
                  {Math.min(currentPage * pagination.limit, pagination.total)}
                </span>{' '}
                из{' '}
                <span className="font-medium">{pagination.total}</span>{' '}
                записей
              </p>
            </div>
            <div>
              <nav
                className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                aria-label="Pagination"
              >
                {/* Кнопка "Назад" */}
                <button
                  onClick={() => pagination.onPageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className={clsx(
                    'relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>

                {/* Номера страниц */}
                {getPageNumbers().map((pageNum, index) => (
                  <React.Fragment key={index}>
                    {pageNum === '...' ? (
                      <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        ...
                      </span>
                    ) : (
                      <button
                        onClick={() => pagination.onPageChange(pageNum as number)}
                        className={clsx(
                          'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                          pageNum === currentPage
                            ? 'z-10 bg-gray-50 border-gray-500 text-gray-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        )}
                      >
                        {pageNum}
                      </button>
                    )}
                  </React.Fragment>
                ))}

                {/* Кнопка "Далее" */}
                <button
                  onClick={() => pagination.onPageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className={clsx(
                    'relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
